import React, { useId } from 'react';

interface BrandLogoProps {
  className?: string;
  size?: number | string;
  animated?: boolean;
}

export default function BrandLogo({ className = 'w-7 h-7', size, animated = false }: BrandLogoProps) {
  const rawId = useId();
  const safeId = rawId.replace(/[^a-zA-Z0-9_-]/g, '');
  const bgGradId = `zp-bg-${safeId}`;
  const goldGradId = `zp-gold-${safeId}`;
  const accentGradId = `zp-accent-${safeId}`;

  const style = size ? { width: size, height: size } : undefined;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 512 512"
      className={`${className} ${animated ? 'animate-pulse' : ''} shrink-0 select-none`}
      style={style}
      aria-label="Zipytiny Logo"
      role="img"
    >
      <defs>
        {/* Background Gradient: Obsidian Dark Charcoal */}
        <linearGradient id={bgGradId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#0f172a" />
          <stop offset="100%" stopColor="#020617" />
        </linearGradient>

        {/* Neon Amber/Gold Gradient for the main icon */}
        <linearGradient id={goldGradId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#fbbf24" />
          <stop offset="50%" stopColor="#f59e0b" />
          <stop offset="100%" stopColor="#ea580c" />
        </linearGradient>

        {/* Glossy Accent Gradient */}
        <linearGradient id={accentGradId} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.15" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* Base Card background with a thin premium gold border */}
      <rect
        x="16"
        y="16"
        width="480"
        height="480"
        rx="108"
        fill={`url(#${bgGradId})`}
        stroke={`url(#${goldGradId})`}
        strokeWidth="10"
      />

      {/* Semi-circular glossy sheen overlay */}
      <path
        d="M 16 124 C 16 64.35, 64.35 16, 124 16 L 388 16 C 447.65 16, 496 64.35, 496 124 L 496 256 C 300 256, 16 350, 16 124 Z"
        fill={`url(#${accentGradId})`}
      />

      {/* Combined Symbol: Play Triangle + Lightning Bolt */}
      <g transform="translate(116, 106)">
        {/* Play shape combined with lightning bolt steps */}
        <path
          d="M 40 40 
             L 240 150 
             L 160 170 
             L 220 260 
             L 60 140 
             L 120 120 
             Z"
          fill={`url(#${goldGradId})`}
        />

        {/* Minimalist summary document lines under/behind the mark to signify Knowledge Engine */}
        <rect x="50" y="210" width="180" height="14" rx="7" fill="#f8fafc" opacity="0.85" />
        <rect x="80" y="240" width="150" height="14" rx="7" fill="#f8fafc" opacity="0.85" />
        <rect x="110" y="270" width="120" height="14" rx="7" fill="#fbbf24" opacity="0.95" />
      </g>
    </svg>
  );
}
